import React, { useRef, useState, useEffect } from "react";
import "../styles/ProfileDialog.css";
import { showAlert } from "../utilities/alertService";
import config from "../services/config";
import Loader from "../utilities/Loader";
import { useNavigate } from "react-router-dom";

const ProfileDialog = ({ open, onClose, onSaveSuccess }) => {
    const [imagePreview, setImagePreview] = useState(null);
    const [cameraStream, setCameraStream] = useState(null);
    const [loading, setLoading] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const navigate = useNavigate();

    // ✅ Define stopCamera before using it
    function stopCamera() {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
            setCameraStream(null);
        }
    }

    const openCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setCameraStream(stream);
        } catch (error) {
            showAlert("error", "Camera access denied!");
        }
    };

    // ✅ Automatically open camera when dialog opens
    useEffect(() => {
        if (open) {
            setImagePreview(null);
            openCamera();
        } else {
            stopCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas size based on video feed to avoid stretching
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the actual video frame on canvas
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    // Now extract the final image
    const imageData = canvas.toDataURL("image/png");

    setImagePreview(imageData);
    stopCamera();
};


    const handleUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
            stopCamera();
        }
    };

    // const handleSave = async () => {
    //     try {
    //         setLoading(true);
    //         const payload = {
    //             user_id: localStorage.getItem("id"),
    //             imageBase64: imagePreview,
    //         };
    //         const res = await config.updateUserProfile(payload);
    //         if (res.status === true) {
    //             showAlert("success", "Profile image saved!");
    //             onSaveSuccess(imagePreview);
    //             handleClose();
    //         } else {
    //             showAlert("error", "Profile not saved!");
    //         }
    //     } catch (err) {
    //         showAlert("error", "Error saving profile!");
    //     } finally {
    //         setLoading(false);
    //     }
    // };
    const handleSave = async () => {
        try {
            setLoading(true);
            const payload = {
                user_id: localStorage.getItem("id"),
                imageBase64: imagePreview,
            };

            const res = await config.updateUserProfile(payload);

            if (res.status === true) {
                showAlert("success", "Profile image saved!");
                onSaveSuccess(imagePreview);
                handleClose();

                // ✅ Navigate to Category page after successful upload
                // const profileData = { image: imagePreview };
                // navigate("/category", { state: { profileData } });
                navigate("/category");
            } else {
                showAlert("error", "Profile not saved!");
            }
        } catch (err) {
            showAlert("error", "Error saving profile!");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        stopCamera();
        setImagePreview(null);
        onClose();
    };

    return (
        <div className="profiledialog">
            <div className="dialog-overlay">
                <div className="dialog-container">
                    {loading && <Loader />}

                    {/* Close Button — now positioned absolutely */}
                    <button className="close-btn" onClick={handleClose}>
                        ✖
                    </button>

                    <div className="dialog-header">
                        <h3>Capture / Upload Image</h3>
                    </div>

                    <div className="dialog-content">
                        {!imagePreview ? (
                            <>
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className="video-preview"
                                />
                                <canvas ref={canvasRef} width="300" height="200" hidden></canvas>
                            </>
                        ) : (
                            <img src={imagePreview} alt="Preview" className="image-preview" />
                        )}
                    </div>

                    <div className="dialog-actions">
                        {!imagePreview ? (
                            <>
                                <button className="btn primary" onClick={captureImage}>
                                    Capture
                                </button>

                                <label htmlFor="upload" className="btn primary">
                                    Upload
                                </label>
                                <input
                                    type="file"
                                    id="upload"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleUpload}
                                />
                            </>
                        ) : (
                            <>
                                <button className="btn primary" onClick={handleSave}>
                                    Save
                                </button>
                                <button
                                    className="btn secondary"
                                    onClick={() => {
                                        setImagePreview(null);
                                        openCamera(); // reopen camera for retake
                                    }}
                                >
                                    Retake
                                </button>
                            </>
                        )}
                    </div>
                    <p className="dialog-note">
                        ** Please update your profile picture to continue.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ProfileDialog;
